import { z } from 'zod';
import { toMinorUnits } from '../utils/money';
import { formatMinorAmount } from '../utils/formatScheduleCurrency';

const PENNY_AMOUNT = /^-?\d+(\.\d{1,2})?$/;

/**
 * Zod schema for the Phase C (#475-C, #597) deposit input. Built per-render
 * from the product's configured minimum deposit and the quote total, since
 * both bounds are runtime values (not static like most schemas in this
 * repo) — see `docs/plans/2026-07-14-payment-schedule-options.md` Phase C.
 *
 * The field is entered by the salesperson in MAJOR units (pounds, matching
 * the legacy `QuotePaymentOptions` pattern) and validated against minor-unit
 * bounds via `toMinorUnits` so the comparison is exact (no float drift).
 *
 * `depositAmount` is `undefined` when the field is empty — that is a valid
 * state (no deposit override; the method's own `elemis_params` default
 * applies, e.g. an equal split), not a validation error. Contract rules
 * (validated client-side to avoid an opaque 422, docs/integrations/
 * elemis-payment-schedule-contract.md §5):
 *   - a supplied deposit must be an exact penny amount — no more than two
 *     decimal places. This enforces the integer-minor-units contract at the
 *     boundary: without it a value like `75.555` would pass the bounds checks
 *     and then be silently rounded by `toMinorUnits()` to a DIFFERENT amount
 *     (`7556` = £75.56) that could enter the preview params and be confirmed
 *     as a deposit the salesperson never typed (contract §5). Checked before
 *     the bounds so a non-penny value is rejected outright and never emitted.
 *   - a supplied deposit must be >= the product's `min_deposit_amount`; when that
 *     minimum is £0, an exact £0 is accepted and means "no deposit"
 *   - a supplied deposit must be < `maxDepositMinor` (D5) — never >=. For
 *     most deposit-bearing methods this bound is the quote total; for
 *     `item_based_with_deposit` (Phase 2, #618) the caller passes the FIRST
 *     cumulative collection target instead (elemis's period-0 `deposit <
 *     first collection target` invariant, elemis #942 / PR #944) — the
 *     `maxDepositLabel` parameter lets the rejection message name whichever
 *     bound is actually in force, without adding a new validation path: the
 *     `< maxDepositMinor` check below is unchanged either way.
 *
 * `inclusiveMaxDisplayMinor` (#621) decouples the DISPLAYED bound from the
 * COMPARISON bound. When the product's configured per-method deposit cap is
 * the binding ceiling, the effective comparison bound is exclusive
 * (`configuredMax + 1`, so a deposit EQUAL to the cap is accepted), but the
 * salesperson must see the INCLUSIVE cap (e.g. £100.00), not the +1 exclusive
 * value (£100.01). Passing the raw inclusive cap here switches the rejection
 * message to an inclusive "must not exceed …" phrasing and displays that
 * amount, while `maxDepositMinor` still drives the `>=` comparison unchanged.
 * Omitted for the quote-total and first-collection-target bounds, which are
 * genuinely exclusive (`deposit < bound`) and keep their existing wording.
 */
export const buildDepositAmountSchema = (
    minDepositMinor: number,
    maxDepositMinor: number,
    currency: string,
    maxDepositLabel = 'the quote total',
    inclusiveMaxDisplayMinor?: number,
) =>
    z.object({
        depositAmount: z
            .number({ invalid_type_error: 'Enter a valid amount' })
            .optional()
            .superRefine((majorAmount, ctx) => {
                if (majorAmount === undefined) {
                    return;
                }

                // String() yields the shortest decimal that round-trips, so the typed digits are tested
                // exactly; rejecting first keeps toMinorUnits() from rounding to an amount never typed.
                if (!PENNY_AMOUNT.test(String(majorAmount))) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Deposit can have at most 2 decimal places (whole pennies only)',
                    });
                    return;
                }

                const minorAmount = toMinorUnits(majorAmount);
                // The floor is the configured minimum; when that is £0, an exact £0 is a valid
                // entry meaning "no deposit".
                const effectiveMin = minDepositMinor;

                if (minorAmount < effectiveMin) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Deposit must be at least ${formatMinorAmount(effectiveMin, currency)}`,
                    });
                }

                if (minorAmount >= maxDepositMinor) {
                    // The comparison is always exclusive (`>= maxDepositMinor`).
                    // Only the message wording/amount changes when an inclusive
                    // cap is in force (#621): show the inclusive cap and phrase
                    // it as "must not exceed", so a configured £100.00 cap is
                    // never misreported to the salesperson as £100.01.
                    const message =
                        inclusiveMaxDisplayMinor !== undefined
                            ? `Deposit must not exceed ${maxDepositLabel} (${formatMinorAmount(inclusiveMaxDisplayMinor, currency)})`
                            : `Deposit must be less than ${maxDepositLabel} (${formatMinorAmount(maxDepositMinor, currency)})`;
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message,
                    });
                }
            }),
    });

export type DepositAmountFormData = z.infer<ReturnType<typeof buildDepositAmountSchema>>;
